import apiClient from '../lib/api/client';
import { getApiUrl, buildApiUrl } from '../config/config';
import { extractErrorMessage } from '../utils/errorHandler';

const createErrorResponse = (error, defaultMessage = 'An unexpected error occurred') => {
  const errorMessage = extractErrorMessage(
    error.response?.data || error,
    defaultMessage
  );

  return {
    isSuccess: false,
    data: null,
    error: {
      message: errorMessage,
      code: error.response?.status || 500,
      originalError: error,
    },
  };
};

const botAutoResolveService = {
  async getBotAutoResolveConfig() {
    try {
      const response = await apiClient.get(getApiUrl('AUTH', 'BOT_AUTO_RESOLVE'));

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to fetch bot auto-resolve configuration' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to fetch bot auto-resolve configuration');
    }
  },

  async updateBotAutoResolveConfig(configData) {
    try {
      const response = await apiClient.patch(getApiUrl('AUTH', 'BOT_AUTO_RESOLVE'), configData);

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to update bot auto-resolve configuration' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to update bot auto-resolve configuration');
    }
  },

  async activateBotAutoResolveConfig() {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'BOT_AUTO_RESOLVE_ACTIVATE'));

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to enable auto-chat resolve by bot' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to enable auto-chat resolve by bot');
    }
  },

  async deactivateBotAutoResolveConfig() {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'BOT_AUTO_RESOLVE_DEACTIVATE'));

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to disable auto-chat resolve by bot' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to disable auto-chat resolve by bot');
    }
  },

  /**
   * Respond to a bot-auto-resolve review card (Take Ownership / Confirm Resolved).
   * POST /api/chats/conversations/{conversationId}/bot-auto-resolve-card/respond/
   *
   * @param {number|string} conversationId - Conversation the card belongs to (from socket).
   * @param {{ cardId: number|string, action: 'accept'|'resolve' }} params
   */
  async respondToBotAutoResolveCard(conversationId, { cardId, action }) {
    try {
      const url = buildApiUrl(`/api/chats/conversations/${conversationId}/bot-auto-resolve-card/respond/`);
      const response = await apiClient.post(url, {
        card_id: cardId,
        action,
      });

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to respond to the review card' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to respond to the review card');
    }
  },
};

export default botAutoResolveService;
